import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { useEffect } from "react";
import { FormProvider } from "react-hook-form";

import { SafetyInfoDocumentModel } from "@/api/mappers/safetyInfoDocument.mapper";
import { NumericField } from "@/components/Form/RHF/FormFields/NumericField/NumericField.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import {
    Translate,
    useGranite
} from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";

import { SafetyDocumentList } from "./documentList/safetyInfoDocumentList.component";
import {
    SafetyInfoFormValidationSchema,
    SafetyInfoFormValues
} from "./safetyInfo.schema";

const FORM_ID = "productSafetyInfoForm";

type SafetyInfoFormProps = {
    isLoading: boolean;
    safetyDocuments: Array<SafetyInfoDocumentModel>;
    onSubmit: (search: number) => void;
    errorStatus?: number;
};

const SafetyInfo = ({
    isLoading,
    safetyDocuments,
    onSubmit,
    errorStatus
}: SafetyInfoFormProps) => {
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {},
        resolver: yupResolver(SafetyInfoFormValidationSchema)
    });

    useEffect(() => {
        if (errorStatus === 404) {
            methods.setError("search", {
                type: "validate",
                message: () => (t: Translate) =>
                    t("form.validation.input.number.safetyInfoSearch")
            });
        }
    }, [errorStatus, methods]);

    const {
        formState: { isValid }
    } = methods;

    const onFormSubmit = ({ search }: SafetyInfoFormValues) => {
        onSubmit(search);
    };

    return (
        <>
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <FormProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onFormSubmit)}
                        >
                            <Row>
                                <Col>
                                    <NumericField<SafetyInfoFormValues>
                                        name="search"
                                        label={t(
                                            "productSafetyInfo.form.search.label"
                                        )}
                                        required
                                        autoComplete={"off"}
                                        placeholder={t(
                                            "productSafetyInfo.form.search.placeholder"
                                        )}
                                        optionalIndication={false}
                                        disabled={isLoading}
                                    />
                                </Col>
                            </Row>
                            <Button
                                test="safetyInfo-button-submit"
                                type={"submit"}
                                color={"success"}
                                disabled={isLoading || !isValid}
                            >
                                {t("productSafetyInfo.form.submit")}
                            </Button>
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
            {safetyDocuments && (
                <SafetyDocumentList documents={safetyDocuments} />
            )}
        </>
    );
};

export { SafetyInfo };
